import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Wallet, Info } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, inr } from "@/lib/api";
import { Empty } from "@/components/common";

export function useQuote(useWallet, delivery = "standard") {
  const { cart, user } = useApp();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const key = JSON.stringify(cart.map((i) => [i.id, i.qty])) + useWallet + delivery + (user?.id || "");
  useEffect(() => {
    if (!cart.length) return setQ(null);
    api.post("/orders/quote", { items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), use_wallet: useWallet, delivery_option: delivery })
      .then((r) => { setQ(r.data); setErr(""); }).catch((e) => setErr(e?.response?.data?.detail || "Could not calculate"));
  }, [key]); // eslint-disable-line
  return [q, err];
}

export function WalletBox({ q, useWallet, setUseWallet }) {
  const { user } = useApp();
  if (!user) return <div className="rounded-2xl bg-[#FDF8E2] p-4 text-sm"><Link to="/login" className="font-semibold text-[#6B1D2F] underline">Log in</Link> to use your wallet rewards.</div>;
  return (
    <div className="rounded-2xl border border-[#E5C378] bg-[#FDF8E2] p-4" data-testid="wallet-box">
      <p className="flex items-center gap-2 font-semibold text-[#6B1D2F]"><Wallet className="h-4 w-4" />Wallet Rewards</p>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between"><span>Wallet Available</span><b data-testid="wallet-available">{inr(q.wallet_available)}</b></div>
        <div className="flex justify-between"><span>Maximum usable on this order ({q.redeem_percent}%)</span><b data-testid="wallet-max-usable">{inr(q.wallet_can_use)}</b></div>
      </div>
      {q.wallet_eligible ? (
        <button disabled={q.wallet_can_use <= 0} onClick={() => setUseWallet(!useWallet)} data-testid="wallet-apply-btn"
          className={`mt-3 w-full ${useWallet ? "btn-ghost" : "btn-saffron"}`}>{useWallet ? `Applied ${inr(q.wallet_used)} · Remove` : `Apply ${inr(q.wallet_can_use)}`}</button>
      ) : <p className="mt-2 text-xs text-[#78716C]">Wallet can be used on orders of {inr(q.min_order_value)} or more.</p>}
      <p className="mt-2 flex gap-1 text-[11px] text-[#78716C]"><Info className="mt-0.5 h-3 w-3 shrink-0" />Wallet balance can cover up to {q.redeem_percent}% of an eligible order's value. Remaining balance stays in your wallet.</p>
    </div>
  );
}

export function Summary({ q, children }) {
  const rows = [["Subtotal", inr(q.subtotal)], ["MRP discount", `−${inr(q.discount)}`], ["Delivery", q.delivery ? inr(q.delivery) : "FREE"], ["Wallet Discount", `−${inr(q.wallet_used)}`]];
  return (
    <div className="card space-y-2 p-5" data-testid="order-summary">
      {rows.map(([a, b]) => <div key={a} className="flex justify-between text-sm"><span className="text-[#78716C]">{a}</span><span>{b}</span></div>)}
      <div className="flex justify-between border-t border-dashed border-[#E7E0D3] pt-3"><span className="font-semibold">Payable Amount</span><span className="font-display text-2xl font-bold text-[#6B1D2F]" data-testid="payable-amount">{inr(q.payable)}</span></div>
      {children}
    </div>
  );
}

export default function Cart() {
  const { cart, setQty } = useApp();
  const [useWallet, setUseWallet] = useState(false);
  const [q, err] = useQuote(useWallet);
  const nav = useNavigate();
  if (!cart.length) return <div className="container-x py-14"><Empty icon="🛍️" title="Your cart is empty" sub="Discover beautiful puja essentials loved by the community." action={<Link to="/shop" className="btn-maroon" data-testid="cart-shop-btn">Explore Products</Link>} /></div>;
  return (
    <div className="container-x py-8">
      <h1 className="font-display mb-6 text-3xl font-bold text-[#6B1D2F]">Your Cart</h1>
      <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-3">
          {cart.map((i) => (
            <div key={i.id} className="card flex gap-4 p-3" data-testid={`cart-item-${i.slug}`}>
              <Link to={`/product/${i.slug}`}><img src={i.image} alt="" className="h-24 w-24 rounded-xl object-cover" /></Link>
              <div className="flex flex-1 flex-col">
                <Link to={`/product/${i.slug}`} className="text-sm font-semibold">{i.name}</Link>
                <p className="text-sm"><b>{inr(i.price)}</b> <s className="text-xs text-[#A8A29E]">{inr(i.mrp)}</s></p>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center rounded-full border border-[#E7E0D3]"><button className="p-2" onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-minus-${i.slug}`}><Minus className="h-3 w-3" /></button><span className="w-6 text-center text-sm">{i.qty}</span><button className="p-2" onClick={() => setQty(i.id, Math.min(20, i.qty + 1))} data-testid={`cart-plus-${i.slug}`}><Plus className="h-3 w-3" /></button></div>
                  <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
                  <button onClick={() => setQty(i.id, 0)} className="text-[#A8A29E] hover:text-red-600" data-testid={`cart-remove-${i.slug}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          {err && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700" data-testid="cart-error">{String(err)}</p>}
          {q && <><WalletBox q={q} useWallet={useWallet} setUseWallet={setUseWallet} />
            <Summary q={q}><button onClick={() => nav("/checkout", { state: { useWallet } })} className="btn-maroon mt-2 w-full !py-3" data-testid="cart-checkout-btn">Proceed to Checkout</button></Summary></>}
        </div>
      </div>
    </div>
  );
}
